import startCase from 'lodash/startCase';

export const StrikeBenchmark = 'strike';

const benchmarkValues: Array<[string, string]> = [
    ['arrival', 'Targets mid-price at start time'],
    ['close', 'Targets close auction price'],
    ['pwap', 'Participation-weighted average price'],
    [StrikeBenchmark, 'Targets mid-price at strike time'],
    ['twap', 'Time-weighted average price'],
    ['vwap', 'Volume-weigthed average price'],
];

export const toDisplayOption = (option: string | undefined) =>
    option ? startCase(option.replace(/-/g, ' ')) : '';

export const toOptions = (values: string[] | undefined) =>
    values === undefined
        ? []
        : values.map((v) => ({
              text: toDisplayOption(v),
              value: v,
          }));

export const benchmarkOptions = toOptions(benchmarkValues.map((b) => b[0]));

export const getBenchmarkTooltip = (benchmark: string | undefined) => {
    if (!benchmark) {
        return undefined;
    }
    const res = benchmarkValues.find((o) => o[0] === benchmark);
    if (res && res[1]) {
        return `${toDisplayOption(benchmark)} - ${res[1]}`;
    }
    return toDisplayOption(benchmark);
};

/* eslint-disable @typescript-eslint/naming-convention */
const ProfileLookupIndex_Profile = 0;
const ProfileLookupIndex_DisplayText = 1;
const ProfileLookupIndex_Tooltip = 2;
const ProfileLookupIndex_GridBox = 3;
/* eslint-enable @typescript-eslint/naming-convention */

const profiles: Record<string, Array<[string, string, string, string]>> = {
    arrival: [
        [
            'adapt-target-very-low',
            'Very low urgency with residuals',
            'Targets rate-based participation bands using very low urgency scale, with impact-based total quantity capping. Will trim order quantity if too impactful (limit depends on low/med/high)',
            'VLOW-R',
        ],
        [
            'adapt-target-low',
            'Low urgency with residuals',
            'Targets rate-based participation bands using low urgency scale, with impact-based total quantity capping. Will trim order quantity if too impactful (limit depends on low/med/high)',
            'LOW-R',
        ],
        [
            'adapt-target-medium',
            'Medium urgency with residuals',
            'Targets rate-based participation bands using medium urgency scale, with impact-based total quantity capping. Will trim order quantity if too impactful (limit depends on low/med/high',
            'MED-R',
        ],
        [
            'adapt-target-high',
            'High urgency with residuals',
            'Targets rate-based participation bands using high urgency scale, with impact-based total quantity capping. Will trim order quantity if too impactful (limit depends on low/med/high)',
            'HIGH-R',
        ],
        [
            'desk-low',
            'Low urgency without residuals',
            'Targets rate-based participation bands using low urgency scale, without impact-based total quantity capping, with no residuals',
            'LOW',
        ],
        [
            'desk-medium',
            'Medium urgency without residuals',
            'Targets rate-based participation bands using a medium urgency scale, without impact-based total quantity capping, with no residuals',
            'MED',
        ],
        [
            'desk-high',
            'High urgency without residuals',
            'Targets rate-based participation bands using high urgency scale, without impact-based total quantity capping, with no residuals',
            'HIGH',
        ],
    ],
    vwap: [
        [
            'vwap-normal-back',
            'Low urgency',
            'Targets vwap normal bands, back-tilted',
            'LOW',
        ],
        [
            'vwap-normal-track',
            'Medium urgency',
            'Targets vwap normal bands, minimal tilt',
            'MED',
        ],
        [
            'vwap-normal-front',
            'High urgency',
            'Targets vwap normal bands, front-tilted',
            'HIGH',
        ],
    ],
    [StrikeBenchmark]: [
        ['desk-low', 'Low urgency without residuals', '', 'LOW'],
        ['desk-medium', 'Medium urgency without residuals', '', 'MED'],
        ['desk-high', 'High urgency without residuals', '', 'HIGH'],
    ],
    twap: [
        [
            'twap-passive',
            'Low urgency',
            'Targets twap normal bands, low urgency',
            'LOW',
        ],
        [
            'twap-normal',
            'Medium urgency',
            'Targets twap normal bands, medium urgency',
            'MED',
        ],
        [
            'twap-aggressive',
            'High urgency',
            'Targets twap normal bands, high urgency',
            'HIGH',
        ],
    ],
    pwap: [
        [
            'pwap-passive',
            'Low urgency',
            'Targets pwap normal bands, low urgency',
            'LOW',
        ],
        [
            'pwap-normal',
            'Medium urgency',
            'Targets pwap normal bands, medium urgency',
            'MED',
        ],
        [
            'pwap-aggressive',
            'High urgency',
            'Targets pwap normal bands, high urgency',
            'HIGH',
        ],
    ],
    close: [
        [
            'desk-low',
            'Low urgency without residuals',
            'Targets rate-based participation bands using low urgency scale, with impact-based total quantity capping, with no residuals',
            'LOW',
        ],
        [
            'desk-medium',
            'Medium urgency without residuals',
            'Targets rate-based participation bands using very medium urgency scale, with impact-based total quantity capping, with no residuals',
            'MED',
        ],
        [
            'desk-high',
            'High urgency without residuals',
            'Targets rate-based participation bands using high urgency scale, with impact-based total quantity capping, with no residuals',
            'HIGH',
        ],
    ],
};

export const getProfileOptions = (benchmark: string) =>
    (profiles[benchmark] ?? []).map((o) => ({
        text: o[ProfileLookupIndex_DisplayText],
        value: o[ProfileLookupIndex_Profile],
    }));

export const getProfileText = (profile: string | undefined) => {
    if (!profile) {
        return undefined;
    }
    for (const key of Object.keys(profiles)) {
        const res = profiles[key].find((o) => o[0] === profile);
        if (res && res[ProfileLookupIndex_DisplayText]) {
            return res[ProfileLookupIndex_DisplayText];
        }
    }
    return toDisplayOption(profile);
};

export const getProfileTooltip = (profile: string | undefined) => {
    if (!profile) {
        return undefined;
    }
    for (const key of Object.keys(profiles)) {
        const res = profiles[key].find((o) => o[0] === profile);
        if (res && res[ProfileLookupIndex_Tooltip]) {
            return `${res[ProfileLookupIndex_DisplayText]} - ${res[ProfileLookupIndex_Tooltip]}`;
        }
    }
    return toDisplayOption(profile);
};
export const getProfileGridBoxText = (profile: string | undefined) => {
    if (!profile) {
        return undefined;
    }
    for (const key of Object.keys(profiles)) {
        const res = profiles[key].find((o) => o[0] === profile);
        if (res) {
            return res[ProfileLookupIndex_GridBox];
        }
    }
    return toDisplayOption(profile);
};
